import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import type { Card, Trick } from '@shared/types/game';
import { CardBack, PlayingCard } from '@/components/PlayingCard';
import Avatar from '@/components/Avatar';
import { seatColor } from '@/lib/labels';

/**
 * The table, seen from your own chair.
 *
 * Your seat is always at the bottom and the others are spread clockwise around
 * the rim, so the layout matches where people actually sit — which is what makes
 * "Mila leads, then me" readable at a glance instead of something to work out
 * from a list.
 *
 * Everything is drawn with gradients and absolute positioning: it scales from a
 * 390px phone to an iPad without a fixed-size image, and the seats stay pinned to
 * the rim at every width.
 */

/**
 * How far out the seats sit, as a percentage of the box.
 *
 * Kept wide on purpose: pulling it in to keep the side badges on screen packed
 * seven of them close enough to overlap each other, which is the same bug wearing
 * a different hat. What keeps them on screen is which way they *grow* — see
 * `anchorFor`. `npm run check:seats` measures both, at 320px as well as 390.
 */
const SEAT_RX = 38;
const SEAT_RY = 36;
/**
 * How long a finished trick stays on the table before being swept up.
 *
 * Long enough to see who took it and what was in it, which at a klaverjas table
 * is not idle curiosity — roem is counted from the four cards that were down
 * together, so the trick *is* the evidence. It was 800ms, which at a brisk table
 * is gone before anyone has looked up.
 */
const TRICK_HOLD_MS = 2200;

/**
 * How far the played cards sit from the middle. Tighter than the seats by a wide
 * margin: the cards are now full size, so at the old radius their outer edge ran
 * into the seat badges on the rim.
 */
const TRICK_RX = 12;
const TRICK_RY = 11;

/**
 * The trick sits a little above the middle of the felt.
 *
 * Your own seat is at the bottom, and the card *you* played lands on that side —
 * so at four players the two met and your own card was half behind your own
 * badge. Above the middle there is nothing in the way: the trump card hangs
 * outside the felt entirely.
 */
const TRICK_CENTRE_Y = 44;

interface Placement {
  /** Degrees, screen coordinates: 90 is the bottom of the table. */
  angle: number;
  x: number;
  y: number;
  /** How far round the side this seat is: -1 hard left, +1 hard right. */
  cos: number;
}

function placeSeat(seat: number, mySeat: number | null, count: number): Placement {
  const visualIndex = mySeat === null ? seat : (seat - mySeat + count) % count;
  const angle = 90 + visualIndex * (360 / count);
  const radians = (angle * Math.PI) / 180;
  const cos = Math.cos(radians);
  return {
    angle,
    cos,
    x: 50 + SEAT_RX * cos,
    y: 50 + SEAT_RY * Math.sin(radians),
  };
}

/**
 * Is this seat far enough round the side to need the narrow badge?
 *
 * At the left and right the badge sits between the screen edge and the middle of
 * the table, and at four players two of those are 110px wide on a 288px table —
 * so the cards in the middle, which is where everyone is looking, end up behind
 * them.
 *
 * So those two stack instead: photo on top, name and numbers under it. That is
 * the narrow way round — the box is as wide as the widest line rather than the sum
 * of them — and it keeps the name, which dropping it would not: two computer
 * players are both a robot face, and at a klaverjas table you need to know which
 * of them is your partner. Top and bottom stay side by side, because there the
 * width is free.
 */
function isSideSeat(cos: number): boolean {
  return Math.abs(cos) > 0.6;
}

/**
 * How much of a seat badge hangs to the left of its point on the rim.
 *
 * A badge centred on a seat at the far right needs half its width to the right of
 * a point that is already near the edge, so it ran off the screen — or worse, got
 * squeezed by the little space left beside it and pushed its own text out of the
 * box, which is what someone actually saw.
 *
 * So the further round the side a seat is, the more its badge leans inward: fully
 * centred at the top and bottom (50%), and 85/15 at the extreme left and right.
 * Not a hard "grow inward" — tried that, and the two side badges then met in the
 * middle of a 320px table. A continuous shift has no threshold to get wrong and
 * leaves both the screen edge and the neighbours alone.
 */
function anchorFor(cos: number): string {
  return `${-(50 + 35 * cos)}%`;
}

/** One seat, as the table needs to draw it. */
export interface TableSeat {
  seat: number;
  name: string;
  avatar: string | null;
  emoji: string | null;
  isConnected: boolean;
  handCount: number;
  /** The card to show on the seat itself, in a blind round. Null hides it. */
  faceUpCard?: Card | null;
  /** The middle line: "2/3" for boerenbridge, the penalty count for hearts. */
  detail?: ReactNode;
  /** A small badge after the detail, such as the dealer's D. */
  badge?: ReactNode;
  /** What a screen reader is told about this seat. */
  spoken?: string;
}

/**
 * Everything the table draws, in a shape neither game owns.
 *
 * The ellipse, the cards flying to the middle, the hold-then-sweep of a
 * finished trick and the resting state during scoring are the same in
 * boerenbridge and hartenjagen — they are about a trick, not about a rule. So
 * each game maps its own state onto this and the animations live in one place.
 */
export interface TableView {
  players: TableSeat[];
  currentTrick: Trick;
  lastTrick: { trick: Trick; winnerSeat: number } | null;
  turnSeat: number | null;
  /** During scoring the last trick rests instead of being swept away. */
  scoring: boolean;
  /** Shown in the middle when there is nothing on the table. */
  emptyLabel: string;
  /** Top-left, outside the felt: the trump card, or the passing direction. */
  corner?: ReactNode;
  /**
   * A line under the last trick when you look back at it — "+20 roem" at a
   * klaverjas table. Optional: the other two games have nothing to add.
   */
  lastTrickNote?: ReactNode;
  /**
   * A short line *on* the felt — how far over or under the bidding went.
   *
   * Centred while there is nothing on the table, and moved to the top edge once
   * cards start landing, because dead centre is exactly where they land.
   */
  note?: ReactNode;
  /** Read out before the seats. */
  spokenHeader?: string;
}

/**
 * How big a face can be before seven of them collide.
 *
 * Boerenbridge seats up to seven around an ellipse that is 390px wide on a
 * phone, so one generous size is not possible — at seven the badges would
 * overlap. Three players get a face worth looking at.
 */
function avatarSize(players: number): number {
  if (players <= 4) return 40;
  if (players === 5) return 34;
  return 28;
}

/**
 * A crowded table gets a leaner badge.
 *
 * "5 kaarten" is the widest line in the box and the least useful thing in it —
 * in boerenbridge everyone holds the same number until a trick is halfway
 * round — and from five seats up it is what makes two badges touch. Measured:
 * at five seats on a 320px phone the upper pair overlapped by eighteen pixels
 * with it and clears without it.
 */
function isCrowded(players: number): boolean {
  return players >= 5;
}

export default function TrickTable({
  view,
  mySeat,
  blind = false,
}: {
  view: TableView;
  mySeat: number | null;
  /** A blind round shows everyone else's card and hides your own. */
  blind?: boolean;
}) {
  const count = view.players.length;
  const faceSize = avatarSize(count);
  const crowded = isCrowded(count);
  const showingTrick = view.currentTrick.length > 0;
  const finished = view.lastTrick ?? null;

  /*
   * A finished trick normally gathers to whoever won it and fades out, which
   * reads well mid-round because another trick follows immediately.
   *
   * While the round is being scored there is no next trick, so the cards stay
   * put instead: this is the only chance anyone gets to see what the last trick
   * actually was, and animating it away leaves an empty table.
   */
  const scoring = view.scoring;

  /*
   * A finished trick is held on the table for a moment before it is swept up, so
   * you can actually see who took it. Without the hold it vanishes the instant
   * the winner leads the next card, which at a fast table is no time at all.
   */
  const trickKey = finished
    ? `${finished.winnerSeat}:${finished.trick.map((p) => `${p.card.suit}${p.card.rank}`).join(',')}`
    : null;

  /*
   * The hold runs whether or not the next card has already landed.
   *
   * It used to bail out the moment a new trick started — and at a klaverjas table
   * the winner leads the next card, which is often a computer player about a
   * second later. So the fourth card of the trick, the one that decided it, was
   * on screen for less than a second and the hold never ran at all. Now the
   * finished trick keeps the middle for its full time and the new card appears
   * after it, which is also the order it happens in on a real table.
   */
  const [holding, setHolding] = useState(false);
  useEffect(() => {
    if (!trickKey) return;
    setHolding(true);
    const timer = window.setTimeout(() => setHolding(false), TRICK_HOLD_MS);
    return () => window.clearTimeout(timer);
  }, [trickKey]);

  /*
   * Looking back on purpose.
   *
   * Even with the hold you can miss it — you were choosing a card, or somebody
   * spoke. So the last trick stays available until the next one lands, which is
   * exactly how long you may look at it on a real table.
   */
  const [peeking, setPeeking] = useState(false);
  useEffect(() => setPeeking(false), [trickKey]);

  const showLast = finished !== null && (peeking || scoring || holding || !showingTrick);
  const trickToShow = showLast ? finished.trick : view.currentTrick;

  // While scoring, the cards rest indefinitely: there is no next trick, and
  // animating them away would leave an empty table for nine seconds.
  const leavingWinner =
    finished && !holding && !peeking && !scoring && !showingTrick ? finished.winnerSeat : undefined;
  const restingWinner = showLast && leavingWinner === undefined ? finished?.winnerSeat : undefined;

  // Cards big enough to read. Past four players the middle fills up, so they
  // step back down.
  const trickSize = count <= 4 ? 'md' : 'sm';

  const winnerPlace = leavingWinner === undefined ? null : placeSeat(leavingWinner, mySeat, count);

  return (
    /*
     * Padding leaves room for the trump card, which sits above the rim.
     *
     * `isolation: isolate` is the load-bearing part: the trump corner sits at
     * z-40 and the seats at z-30, and without a stacking context of their own
     * they scrolled straight over the page's sticky header at z-20. A wall
     * rather than lower numbers, so the next thing with a z-index inherits the
     * fix instead of re-discovering the bug.
     */
    <div className="relative w-full pt-14 [isolation:isolate] md:pt-20">
      {/* Rim, then felt inside it. */}
      <div className="table-rim relative aspect-[5/4] w-full rounded-[50%] p-[3.5%] md:aspect-[16/11]">
        <div className="felt relative h-full w-full rounded-[50%]">
          {/* The trick in the middle. Each card sits toward whoever played it. */}
          <div className="absolute inset-0">
            {trickToShow.map((played, index) => {
              const place = placeSeat(played.seat, mySeat, count);
              const radians = (place.angle * Math.PI) / 180;
              const x = 50 + TRICK_RX * Math.cos(radians);
              const y = TRICK_CENTRE_Y + TRICK_RY * Math.sin(radians);
              const tilt = ((place.angle + 90) % 360) / 24 - 7;

              // Where the card comes from, and where a finished trick goes to.
              const vars = {
                '--from-x': `${Math.cos(radians) * 60}px`,
                '--from-y': `${Math.sin(radians) * 60}px`,
                '--tilt': `${tilt}deg`,
                '--to-x': winnerPlace
                  ? `${Math.cos((winnerPlace.angle * Math.PI) / 180) * 90}px`
                  : '0px',
                '--to-y': winnerPlace
                  ? `${Math.sin((winnerPlace.angle * Math.PI) / 180) * 90}px`
                  : '0px',
                left: `${x}%`,
                top: `${y}%`,
                zIndex: 10 + index,
              } as CSSProperties;

              return (
                <div
                  key={`${played.seat}-${played.card.suit}-${played.card.rank}`}
                  className={[
                    'absolute -translate-x-1/2 -translate-y-1/2',
                    leavingWinner !== undefined
                      ? 'animate-trick-away'
                      : scoring
                        ? ''
                        : 'animate-card-to-table',
                  ].join(' ')}
                  style={vars}
                >
                  <PlayingCard
                    card={played.card}
                    size={trickSize}
                    // Ring the winning card while the round is being counted, so
                    // "who took that?" is answered on the table itself.
                    className={
                      restingWinner === played.seat
                        ? 'ring-2 ring-accent-400 ring-offset-2 ring-offset-transparent'
                        : ''
                    }
                  />
                </div>
              );
            })}

            {trickToShow.length === 0 && (
              <p className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-center text-xs text-emerald-100/50">
                {view.emptyLabel}
              </p>
            )}

            {/*
              Dead centre while the table is empty — the bidding phase, when this
              line matters most.

              The moment cards land it moves out to the corner instead of to the
              top of the felt, where it used to go: at four players the seat
              opposite you sits exactly there, and the line disappeared behind that
              badge. Same lesson as the last-trick caption.
            */}
            {view.note && trickToShow.length === 0 && (
              <p className="absolute left-1/2 top-[38%] -translate-x-1/2 whitespace-nowrap rounded-full bg-black/45 px-2.5 py-1 text-xs text-emerald-100 backdrop-blur">
                {view.note}
              </p>
            )}
          </div>
        </div>
      </div>

      {/*
        The corner sits outside the felt rather than on it: on a phone the table
        is only ~300px across and anything in the corner ends up underneath a
        seat. What goes here is the game's business — a trump card, or which way
        the cards are being passed.
      */}
      {view.corner && (
        <div className="absolute left-0 top-0 flex items-center gap-2" style={{ zIndex: 40 }}>
          {view.corner}
        </div>
      )}

      {/*
        Opposite the trump card, outside the felt.
        On the felt there is no spot that is free at every table size — at seven
        players the seats are all the way round — and up here nothing else lives.
      */}
      {(finished || view.note) && (
        <div
          className="absolute right-0 top-0 flex flex-col items-end gap-1"
          style={{ zIndex: 40 }}
        >
          {view.note && trickToShow.length > 0 && (
            <p className="whitespace-nowrap rounded-full bg-black/45 px-2.5 py-1 text-xs text-emerald-100 backdrop-blur">
              {view.note}
            </p>
          )}

          {finished && (
            <button
              type="button"
              onClick={() => setPeeking((current) => !current)}
              aria-pressed={peeking}
              className="rounded-full bg-base-900/80 px-2.5 py-1 text-xs text-slate-300 backdrop-blur hover:text-white"
            >
              {peeking ? 'sluiten' : 'laatste slag'}
            </button>
          )}

          {/*
            Who took it, next to the button rather than on the felt.
            It used to sit at the top of the green, which at four players is
            exactly where the seat opposite you is — so the sentence ran behind a
            badge. Out here nothing else lives.
          */}
          {restingWinner !== undefined && (
            <p className="animate-pop-in whitespace-nowrap rounded-full bg-black/45 px-2.5 py-1 text-xs text-emerald-100">
              slag voor {view.players[restingWinner]?.name}
              {view.lastTrickNote ? <> · {view.lastTrickNote}</> : null}
            </p>
          )}
        </div>
      )}

      {/*
        Seats on the rim. Positioned within the table area only — hence the
        inset that skips the top padding where the trump card lives.
      */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 top-14 md:top-20">
        {view.players.map((player) => {
          const place = placeSeat(player.seat, mySeat, count);
          const isTurn = view.turnSeat === player.seat;
          const isMe = player.seat === mySeat;
          const narrow = isSideSeat(place.cos);

          return (
            <div
              key={player.seat}
              className="absolute"
              style={{
                left: `${place.x}%`,
                top: `${place.y}%`,
                transform: `translate(${anchorFor(place.cos)}, -50%)`,
                zIndex: 30,
              }}
            >
              <div
                data-seat-badge
                className={[
                  /*
                   * `w-max`, and a cap on it.
                   *
                   * An absolutely positioned box shrinks to the space left between
                   * its offset and the edge of its container, so the seat nearest
                   * the right had less room than its own contents needed and the
                   * text came out of the box. `w-max` sizes it to what is in it
                   * instead; `max-w` stops a long name from making it enormous, and
                   * `overflow-hidden` means anything that still does not fit is
                   * clipped by the rounded box rather than spilling over the felt.
                   */
                  'flex w-max max-w-[7.5rem] flex-col items-center gap-1 overflow-hidden',
                  'rounded-xl px-2 py-1.5 backdrop-blur md:max-w-[9.5rem]',
                  'border md:px-3',
                  isTurn
                    ? 'animate-turn-ring border-accent-500/60 bg-base-900/90'
                    : 'border-white/10 bg-base-900/75',
                  !player.isConnected ? 'opacity-50' : '',
                ].join(' ')}
              >
                {/*
                  Face on the left, everything about the player to the right of it.
                  Stacked the other way round — name beside the photo, numbers
                  underneath — the badge grew taller than it was wide, and on a
                  seven-seat ellipse height is the dimension that runs out first.
                */}
                <span
                  className={
                    narrow ? 'flex flex-col items-center gap-0.5' : 'flex items-center gap-1.5'
                  }
                >
                  {/* The seat colour as a ring, never as a fill: SEAT_COLORS was
                    validated against a dark background, and using those same
                    colours behind a face is outside what that check covers. */}
                  <Avatar
                    player={{
                      name: player.name,
                      emoji: player.emoji ?? undefined,
                      avatar: player.avatar,
                    }}
                    size={faceSize}
                    seatColor={seatColor(player.seat)}
                  />

                  <span
                    className={`flex min-w-0 flex-col leading-tight ${
                      narrow ? 'items-center' : 'items-start'
                    }`}
                  >
                    <span
                      className={`truncate text-xs font-medium ${
                        narrow ? 'max-w-[3.5rem]' : 'max-w-[4.5rem] md:max-w-[6rem] md:text-sm'
                      }`}
                    >
                      {isMe ? 'jij' : player.name}
                    </span>

                    <span className="flex items-center gap-1.5 text-xs tabular-nums text-slate-300">
                      {player.detail ?? <span className="text-slate-500">—</span>}
                      {player.badge}
                    </span>

                    {/* No wrapping: "5 kaarten" broke over two lines on the side
                        seats, which made those badges a third taller than the
                        others for no reason. The comment sits above the
                        conditional, not inside it — a comment in the same slot as
                        the element is a second expression and will not parse. */}
                    {!crowded && !narrow && !blind && player.handCount > 0 && !isMe && (
                      <span className="whitespace-nowrap text-[0.65rem] leading-none text-slate-500">
                        {player.handCount} {player.handCount === 1 ? 'kaart' : 'kaarten'}
                      </span>
                    )}
                  </span>
                </span>

                {/* In a blind round the others' cards are the whole point, so show
                  them right on the seat where you would really be looking. */}
                {blind &&
                  (isMe ? (
                    <CardBack size="sm" className="mt-1 !h-14 !w-10 md:!h-20 md:!w-14" />
                  ) : player.faceUpCard ? (
                    <PlayingCard
                      card={player.faceUpCard}
                      size="sm"
                      className="mt-1 !h-14 !w-10 md:!h-20 md:!w-14"
                    />
                  ) : null)}
              </div>
            </div>
          );
        })}
      </div>

      <p className="sr-only">
        {view.spokenHeader ? `${view.spokenHeader} ` : ''}Aan tafel:{' '}
        {view.players.map((player) => player.spoken ?? player.name).join('; ')}.
      </p>
    </div>
  );
}
